import React, { useEffect, useMemo } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  InputNumber,
  Row,
  Col,
} from 'antd';
import dayjs from 'dayjs';
import type { ServiceCharge } from '../../services/serviceChargeService';

interface ServiceChargeFormProps {
  open: boolean;
  onCancel: () => void;
  onSubmit: (values: any) => void;
  loading: boolean;
  initialValues?: ServiceCharge | null;
  users: any[];
  services: any[];
}

const ServiceChargeForm: React.FC<ServiceChargeFormProps> = ({
  open,
  onCancel,
  onSubmit,
  loading,
  initialValues,
  users,
  services,
}) => {
  const [form] = Form.useForm();

  // ✅ สร้าง service options พร้อม price
  const serviceOptions = useMemo(() => {
    return services.map(service => ({
      label: `${service.name} (${service.price?.toLocaleString() || 0} ກີບ)`,
      value: service._id,
      price: service.price, // ✅ เก็บ price ไว้ใน option
    }));
  }, [services]);

  useEffect(() => {
    if (open && initialValues) {
      form.setFieldsValue({
        ...initialValues,
        userId: initialValues.userId._id,
        serviceId: initialValues.serviceId._id,
        date: dayjs(initialValues.date),
      });
    } else if (open) {
      form.resetFields();
      form.setFieldsValue({
        date: dayjs(),
      });
    }
  }, [open, initialValues, form]);

  // ✅ Handler เมื่อเลือก service
  const handleServiceChange = (serviceId: string) => {
    // หา service ที่เลือก
    const selectedService = services.find(s => s._id === serviceId);
    
    if (selectedService) {
      // ตั้งค่า price อัตโนมัติ
      form.setFieldsValue({
        price: selectedService.price,
      });
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      onSubmit({
        ...values,
        date: values.date.format('YYYY-MM-DD'),
      });
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  return (
    <Modal
      title={initialValues ? 'ແກ້ໄຂຂໍ້ມູນ' : 'ເພີ່ມຂໍ້ມູນໃໝ່'}
      open={open}
      onCancel={onCancel}
      onOk={handleSubmit}
      confirmLoading={loading}
      width={600}
      okText={initialValues ? 'ແກ້ໄຂ' : 'ເພີ່ມ'}
      cancelText="ຍົກເລີກ"
    >
      <Form
        form={form}
        layout="vertical"
        autoComplete="off"
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="userId"
              label="ພະນັກງານ"
              rules={[{ required: true, message: 'ກະລຸນາເລືອກພະນັກງານ' }]}
            >
              <Select
                placeholder="ເລືອກພະນັກງານ"
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                }
                options={users.map(user => ({
                  label: user.name,
                  value: user._id,
                }))}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="serviceId"
              label="ປະເພດບໍລິການ"
              rules={[{ required: true, message: 'ກະລຸນາເລືອກປະເພດບໍລິການ' }]}
            >
              <Select
                placeholder="ເລືອກບໍລິການ"
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                }
                onChange={handleServiceChange} // ✅ เพิ่ม onChange
                options={serviceOptions}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="type"
              label="ປະເພດ"
              rules={[{ required: true, message: 'ກະລຸນາເລືອກປະເພດ' }]}
            >
              <Select
                placeholder="ເລືອກປະເພດ"
                options={[
                  { label: 'ຄ່າບໍລິການ', value: 'service' },
                  { label: 'ຄ່າແນະນຳ', value: 'recommend' },
                ]}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="price"
              label="ລາຄາ (ກີບ)"
              rules={[
                { required: true, message: 'ກະລຸນາປ້ອນລາຄາ' },
                { type: 'number', min: 0, message: 'ລາຄາຕ້ອງບໍ່ຕ່ຳກວ່າ 0' },
              ]}
            >
              <InputNumber
                placeholder="ປ້ອນລາຄາ"
                style={{ width: '100%' }}
                formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={value => value!.replace(/\$\s?|(,*)/g, '') as any}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="date"
          label="ວັນທີ່"
          rules={[{ required: true, message: 'ກະລຸນາເລືອກວັນທີ່' }]}
        >
          <DatePicker
            style={{ width: '100%' }}
            format="DD/MM/YYYY"
            disabledDate={(current) => current && current > dayjs().endOf('day')}
          />
        </Form.Item>

        <Form.Item
          name="note"
          label="ໝາຍເຫດ"
        >
          <Input.TextArea
            placeholder="ໝາຍເຫດ (ຖ້າມີ)"
            rows={3}
            maxLength={500}
            showCount
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ServiceChargeForm;